import { MoneyIcon } from "@images/svgIcons/moneyIcon"
import { NotepadIcon } from "@images/svgIcons/notepadIcon"
import { useCurrentBox } from "@modules/box/useBox"
import { BoxOfferFormPageProps } from "@modules/boxOffer/components/boxOfferForm"
import {
  BoxOfferFormAlertCard,
  BoxOfferFormSection,
  BoxOfferInputCustomLabel,
} from "@modules/boxOffer/components/boxOfferFormSection"
import {
  BOX_OFFER_TICKET_BOOKS_MAX,
  BOX_OFFER_TICKET_BOOKS_MIN,
  BOX_OFFER_VALIDITY_MAX,
  BOX_OFFER_VALIDITY_MIN,
  BoxOfferPriceType,
  BoxOfferType,
  BoxOfferValidity,
} from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOfferSidebar } from "@modules/boxOffer/hooks/useBoxOfferSidebar"
import { word } from "@modules/core/utils/i18n"
import { currencySymbol, formatCurrency } from "@modules/currency/currency"
import { FinanceFeesAlertCard } from "@modules/finances/components/financeFeesAlertCard"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { StripeAccountStatuEnum } from "@modules/payment/stripeTypes"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { capitalize } from "@modules/utils/stringUtils"
import { DropdownChangeEvent } from "primereact/dropdown"
import { useEffect, useMemo } from "react"
import { useTheme } from "styled-components"

export const BoxOfferFormFirstPage = ({
  formik,
  isFormFieldInValid,
  getFormErrorMessage,
  handleSubmit,
}: BoxOfferFormPageProps) => {
  const theme = useTheme()
  const language = useLanguage()
  const { result: box } = useCurrentBox()
  const { hideSidebar } = useBoxOfferSidebar()
  const priceOptions = [
    // Always put the free option first, it's the default value if the user is on a lite version
    { value: BoxOfferPriceType.Free, label: word("offer.form.price_switch.free") },
    { value: BoxOfferPriceType.Paid, label: word("offer.form.price_switch.price") },
  ]

  const isBoxOfferLiteVersion = useMemo(
    () =>
      box &&
      (box.stripeAccountStatus === StripeAccountStatuEnum.REQUIRE_ADDITIONAL_INFORMATION ||
        box.stripeAccountStatus === StripeAccountStatuEnum.UNDEFINED)
        ? true
        : false,
    [box],
  )

  // Set the price switch to free if the box offer is a lite version, it's necessary to set the drop-in to free
  useEffect(() => {
    if (isBoxOfferLiteVersion) formik.setFieldValue("priceSwitch", BoxOfferPriceType.Free)
  }, [isBoxOfferLiteVersion])

  const displayPrice = useMemo(
    () =>
      ![BoxOfferType.Trial, BoxOfferType.Millenium].includes(formik.values.type) ||
      (BoxOfferType.Trial === formik.values.type && formik.values.priceSwitch === BoxOfferPriceType.Paid),
    [formik.values.priceSwitch, formik.values.type],
  )

  const priceLabel = useMemo(() => {
    return formik.values.type === BoxOfferType.DropIn || formik.values.type === BoxOfferType.Trial
      ? word(`offer.form.${formik.values.type}.price.label`)
      : word("offer.form.price.label")
  }, [formik.values.type])

  const isMilleniumOrDropInLiteVersion =
    formik.values.type === BoxOfferType.Millenium ||
    (formik.values.type === BoxOfferType.DropIn && isBoxOfferLiteVersion) ||
    (formik.values.type === BoxOfferType.Trial && isBoxOfferLiteVersion)

  return (
    <div className="flex flex-column gap-6 pb-4">
      <div className="flex flex-column gap-2">
        <HUText fontStyle="TL">{`${word(`offer.form.${formik.values.type}.create`)} (1/3)`}</HUText>
        <HUText fontStyle="BM" color={theme.neutralColor700}>
          {word(`offer.form.${formik.values.type}.subtitle`)}
        </HUText>
      </div>
      <div>
        <BoxOfferFormSection
          title={word("box.offer.form.infos.generalInformation")}
          icon={<NotepadIcon color={theme.mainWhite} />}
          className="mb-5"
        />
        <FormikInput
          flex
          label={word("offer.form.title.label")}
          isRequiredInput
          getFieldProps={formik.getFieldProps}
          isInvalid={isFormFieldInValid("name")}
          name="name"
          placeHolder={word("offer.form.title.placeholder")}
          error={getFormErrorMessage("name")}
        />
        <FormikInput
          isRequiredInput
          label={word("offer.form.description.label")}
          getFieldProps={formik.getFieldProps}
          isInvalid={isFormFieldInValid("description")}
          name="description"
          error={getFormErrorMessage("description")}
          type="textarea"
          formikStyle={{ flex: 1 }}
          placeHolder={word("offer.form.description.placeholder")}
        />
      </div>
      {formik.values.type === BoxOfferType.TicketsBook && (
        <div>
          <BoxOfferFormSection
            title={word("box.offer.form.quantity")}
            icon="ticket"
            description={word("offer.form.numberOfSessions.TICKETS_BOOK.description")}
            className="mb-5"
          />
          <FormikInput
            formikStyle={{ flex: 1 }}
            label={word("offer.form.numberOfSessions.title")}
            isRequiredInput
            isInvalid={isFormFieldInValid("count")}
            getFieldProps={formik.getFieldProps}
            name="count"
            error={getFormErrorMessage("count")}
            type="number"
            numberProps={{
              placeholder: word("offer.form.numberOfSessions.title"),
              min: BOX_OFFER_TICKET_BOOKS_MIN,
              max: BOX_OFFER_TICKET_BOOKS_MAX,
              maxFractionDigits: 0,
            }}
            setFieldValue={formik.setFieldValue}
          />
        </div>
      )}
      <div className="flex flex-column gap-3">
        {!isBoxOfferLiteVersion && (
          <BoxOfferFormSection
            title={word("box.offer.form.price.title")}
            icon={<MoneyIcon color={theme.mainWhite} />}
            className="mb-3"
            description={
              formik.values.type !== BoxOfferType.Millenium
                ? word(`box.offer.form.price.${formik.values.type}.subtitle`)
                : undefined
            }
          />
        )}
        {isMilleniumOrDropInLiteVersion && (
          <div className="flex flex-column gap-5">
            <div className="flex flex-column gap-2">
              <BoxOfferInputCustomLabel label={word("box.offer.form.price.title")} margin="" />
              <BoxOfferFormAlertCard
                title={word("box.offer.form.alert.free.title")}
                description={
                  isBoxOfferLiteVersion
                    ? word("box.offer.form.alert.free.liteVersion")
                    : word("box.offer.form.alert.free.description")
                }
              />
            </div>
            {formik.values.type === BoxOfferType.Millenium && (
              <div className="flex flex-column gap-2">
                <BoxOfferInputCustomLabel label={word("box.offer.form.minimum_monthly_duration.label")} margin="" />
                <BoxOfferFormAlertCard
                  title={word("box.offer.form.alert.commitment.title")}
                  description={word("box.offer.form.alert.commitment.description")}
                />
              </div>
            )}
          </div>
        )}
        {formik.values.type === BoxOfferType.Trial && (
          <>
            <FormikInput
              disabled={isBoxOfferLiteVersion}
              formikStyle={{ flex: 1 }}
              type="dropdown"
              label={word(`offer.form.TRIAL.price.label`)}
              name="priceSwitch"
              getFieldProps={formik.getFieldProps}
              isInvalid={isFormFieldInValid("priceSwitch")}
              error={getFormErrorMessage("priceSwitch")}
              dropdownProps={{
                options: priceOptions,
                value: formik.values.priceSwitch,
                onChange(event: DropdownChangeEvent) {
                  if (event.target.value === BoxOfferPriceType.Free) {
                    formik.setFieldValue("price", undefined, false)
                  }
                },
              }}
              setFieldValue={formik.setFieldValue}
            />
          </>
        )}
        {displayPrice && !isBoxOfferLiteVersion && (
          <>
            <FormikInput
              disabled={isBoxOfferLiteVersion}
              label={priceLabel}
              isInvalid={isFormFieldInValid("price")}
              getFieldProps={formik.getFieldProps}
              name="price"
              error={getFormErrorMessage("price")}
              type="number"
              numberProps={{
                currency: box?.stripeAccountCurrency,
                mode: "currency",
                step: 0.1,
                min: 0,
                placeholder: word("offer.form.price.placeholder", {
                  minPrice: formatCurrency(box?.stripeMinimumAmount ?? 0, box?.stripeAccountCurrency, language),
                }),
              }}
              setFieldValue={formik.setFieldValue}
              formikStyle={{ flex: 1, maxWidth: "100%", marginBottom: 0 }}
              rightButton={
                <HUText fontStyle="TM">
                  {formik.values.type === BoxOfferType.Subscription
                    ? `${currencySymbol(box?.stripeAccountCurrency, language)} / ${word("global.month.label", { count: 1 })}`
                    : `${currencySymbol(box?.stripeAccountCurrency, language)}`}
                </HUText>
              }
            />
            <FinanceFeesAlertCard />
          </>
        )}
        {formik.values.type === BoxOfferType.Subscription && (
          <div className="flex flex-column gap-3">
            <FormikInput
              formikStyle={{ flex: 1, maxWidth: "80%" }}
              isRequiredInput
              label={word("box.offer.form.minimum_monthly_duration.label")}
              type="number"
              name="minimumMonthlyDuration"
              getFieldProps={formik.getFieldProps}
              isInvalid={isFormFieldInValid("minimumMonthlyDuration")}
              error={getFormErrorMessage("minimumMonthlyDuration", word("global.form.required"))}
              setFieldValue={formik.setFieldValue}
              numberProps={{
                min: 1,
                max: 24,
                placeholder: word("offer.form.minimumMonthlyDuration.placeholder"),
                maxFractionDigits: 0,
              }}
              rightButton={
                <HUText fontStyle="TM">
                  {capitalize(word("global.month.label", { count: formik.values.minimumMonthlyDuration ?? 1 }))}
                </HUText>
              }
            />
            <BoxOfferFormAlertCard
              title={word("offer.form.subscription.alert_title")}
              description={word("offer.form.subscription.alert_description")}
            />
          </div>
        )}
      </div>
      {(formik.values.type === BoxOfferType.Trial ||
        formik.values.type === BoxOfferType.TicketsBook ||
        formik.values.type === BoxOfferType.DropIn) && (
        <div>
          <BoxOfferFormSection
            title={word("box.offer.form.validity.title")}
            icon="calendar"
            description={word(`box.offer.form.validity.${formik.values.type}.description`)}
            className="mb-5"
          />
          <FormikInput
            formikStyle={{ flex: 1 }}
            label={word("box.offer.form.validity.title")}
            type="dropdown"
            name="validitySwitch"
            getFieldProps={formik.getFieldProps}
            setFieldValue={formik.setFieldValue}
            isInvalid={isFormFieldInValid("validitySwitch")}
            dropdownProps={{
              options: Object.values(BoxOfferValidity).map((val) => ({
                label: word(`box.offer.form.infos.validity.types.${val}`),
                value: val,
              })),
              value: formik.values.validitySwitch,
              onChange(event: DropdownChangeEvent) {
                if (event.target.value === BoxOfferValidity.UNLIMITED) {
                  formik.setFieldValue("validityPeriodMonths", undefined, false)
                }
              },
            }}
          />
          {formik.values.validitySwitch === BoxOfferValidity.DEFINED && (
            <FormikInput
              formikStyle={{ flex: 1 }}
              isInvalid={isFormFieldInValid("validityPeriodMonths")}
              getFieldProps={formik.getFieldProps}
              name="validityPeriodMonths"
              error={getFormErrorMessage("validityPeriodMonths")}
              type="number"
              numberProps={{
                min: BOX_OFFER_VALIDITY_MIN,
                max: BOX_OFFER_VALIDITY_MAX,
                placeholder: word("offer.form.minimumMonthlyDuration.placeholder"),
                maxFractionDigits: 0,
              }}
              setFieldValue={formik.setFieldValue}
              rightButton={
                <HUText fontStyle="TM">
                  {capitalize(word("global.month.label", { count: formik.values.validityPeriodMonths ?? 1 }))}
                </HUText>
              }
            />
          )}
        </div>
      )}
      <div className="flex gap-5 mt-5 mb-4">
        <HUButton
          text={word("global.cancel")}
          onClick={() => hideSidebar()}
          type="Rounded"
          size="M"
          colorType="Quaternary"
          fullWidth
        />
        <HUButton
          text={word("global.next")}
          onClick={handleSubmit}
          type="Right_Icon"
          size="M"
          colorType="Primary"
          fullWidth
          icon={{
            iconView: <span className={`pi pi-arrow-right`} style={{ color: theme.mainWhite }} />,
          }}
        />
      </div>
    </div>
  )
}
